"use client";

import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { formatArabicDate, truncateText } from "@/app/funcs/funcs";
import ViewToggle, { getStoredViewMode, setStoredViewMode } from "./components/ViewToggle";
import {
  fetchAuthorInfo,
  fetchAuthorPostsCount,
  getData,
  getDataWithPagination
} from "@/app/funcs/api";

function stripHtml(html = "") {
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

/** Category IDs for REST list filtering: top-level `categories` and/or `_embedded.wp:term`. */
function getItemCategoryIds(item) {
  const ids = new Set();
  const root = item?.categories;
  if (Array.isArray(root)) {
    for (const id of root) {
      const n = Number(id);
      if (Number.isFinite(n)) ids.add(n);
    }
  }
  const terms = item?._embedded?.["wp:term"];
  const flat = Array.isArray(terms) ? terms.flat() : [];
  for (const term of flat) {
    if (term?.taxonomy === "category" && term.id != null) {
      const n = Number(term.id);
      if (Number.isFinite(n)) ids.add(n);
    }
  }
  return Array.from(ids);
}

export default function SectionGridList({
  postType,
  pageTitle,
  description,
  hrefBase,
  searchPlaceholder = "ابحث...",
  emptyMessage = "لا يوجد عناصر متاحة حالياً.",
  fallbackIcon = "bi-grid",
  imageMaxHeight = "220px",
  imageFit = "cover",
  titleActions = null,
  belowTitle = null,
  blockedPostStatuses = [],
  blockedItemMessage = "انتهى هذا التحدي ولم يعد متاحًا للعرض.",
  blockItem = null,
  /** Optional render slot below author row on each card: (item) => ReactNode */
  cardFooter = null
}) {
  const pageSize = 9;
  const [items, setItems] = useState([]);
  const [authorsData, setAuthorsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [usingFallback, setUsingFallback] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setViewMode(getStoredViewMode());
  }, []);

  const handleViewChange = (mode) => {
    setViewMode(mode);
    setStoredViewMode(mode);
  };

  const fetchAuthorsData = async (posts) => {
    const authorIds = [...new Set(posts.map((post) => post.author).filter(Boolean))];
    if (!authorIds.length) return;

    const authorEntries = await Promise.all(
      authorIds.map(async (authorId) => {
        try {
          const [authorInfo, postsCount] = await Promise.all([
            fetchAuthorInfo(authorId),
            fetchAuthorPostsCount(authorId, postType)
          ]);
          return [authorId, { ...authorInfo, postsCount }];
        } catch {
          return [
            authorId,
            {
              name: "مستخدم غير معروف",
              postsCount: 0
            }
          ];
        }
      })
    );

    const nextAuthors = Object.fromEntries(authorEntries);
    setAuthorsData((prev) => ({ ...prev, ...nextAuthors }));
  };

  const fetchItems = async (page, append = false) => {
    try {
      setLoading(true);
      if (!append) {
        setError(null);
        setUsingFallback(false);
      }

      const response = await getDataWithPagination(postType, page, pageSize, "_embed");
      const posts = Array.isArray(response?.data) ? response.data : [];

      if (append && posts.length === 0) {
        setHasMore(false);
        setCurrentPage(page);
        return;
      }

      setItems((prev) => (append ? [...prev, ...posts] : posts));
      fetchAuthorsData(posts);

      const nextPage = Number(response?.currentPage || page);
      setCurrentPage(nextPage);

      const total = Number(response?.total || 0);
      const totalPagesHdr = Number(response?.totalPages || 0);
      let hasNext;
      if (total > 0) {
        hasNext = nextPage * pageSize < total;
      } else if (totalPagesHdr > 0) {
        hasNext = nextPage < totalPagesHdr;
      } else {
        hasNext = posts.length === pageSize;
      }
      setHasMore(hasNext);
    } catch (primaryError) {
      try {
        const fallbackData = await getData(postType, "_embed");
        const fallbackPosts = Array.isArray(fallbackData) ? fallbackData : [];
        setItems(fallbackPosts);
        fetchAuthorsData(fallbackPosts);
        setHasMore(false);
        setCurrentPage(1);
        setUsingFallback(true);
        setError(primaryError?.message || "تعذر تحميل البيانات من المصدر الرئيسي");
      } catch {
        setError(primaryError?.message || "حدث خطأ في تحميل البيانات");
        setItems([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setItems([]);
    setAuthorsData({});
    setCurrentPage(1);
    setHasMore(true);
    fetchItems(1, false);
  }, [postType]);

  useEffect(() => {
    if (!hasMore || loading || !sentinelRef.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) {
          fetchItems(currentPage + 1, true);
        }
      },
      { rootMargin: "250px" }
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [currentPage, hasMore, loading]);

  const categories = useMemo(() => {
    const categoryMap = new Map();
    for (const item of items) {
      const terms = item?._embedded?.["wp:term"] || [];
      const flatTerms = terms.flat();
      for (const term of flatTerms) {
        if (term?.taxonomy === "category" && term?.id && term?.name) {
          categoryMap.set(term.id, term.name);
        }
      }
    }

    return [
      { id: "all", name: "الكل" },
      ...Array.from(categoryMap.entries()).map(([id, name]) => ({ id, name }))
    ];
  }, [items]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return items.filter((item) => {
      const catIds = getItemCategoryIds(item);
      const selectedNum = Number(selectedCategory);
      const inCategory =
        selectedCategory === "all" ||
        (Number.isFinite(selectedNum) && catIds.includes(selectedNum));
      if (!inCategory) return false;
      if (!normalizedSearch) return true;

      const titleText = stripHtml(item?.title?.rendered || "").toLowerCase();
      const excerptText = stripHtml(item?.excerpt?.rendered || "").toLowerCase();
      return titleText.includes(normalizedSearch) || excerptText.includes(normalizedSearch);
    });
  }, [items, searchTerm, selectedCategory]);

  const AuthorInfo = ({ authorId }) => {
    const author = authorsData[authorId];
    if (!author) return null;

    const authorName =
      author.name ||
      author.user_display_name ||
      author.user_login ||
      author.name?.toLowerCase().replace(/\s+/g, "");
    const authorSlug =
      author.slug || author.user_login || author.name?.toLowerCase().replace(/\s+/g, "");
    const profileUrl = authorSlug ? `/@${authorSlug}` : "#";
    const authorAvatar = author?.acf?.avatar_id?.url
      ? author.acf.avatar_id.sizes?.thumbnail
      : author?.avatar_urls?.[96] || null;

    return (
      <div className="d-flex align-items-center mt-2 rounded-pill py-1 px-2 bg-white">
        {authorAvatar ? (
          <img
            src={authorAvatar}
            alt={authorName}
            className="rounded-circle me-2"
            width="24"
            height="24"
          />
        ) : (
          <div
            className="bg-secondary rounded-circle me-2 d-flex align-items-center justify-content-center"
            style={{ width: "24px", height: "24px" }}
          >
            <i className="bi bi-person text-white" style={{ fontSize: "12px" }}></i>
          </div>
        )}
        <Link href={profileUrl} className="text-decoration-none">
          <small className="text-dark fw-bold">{authorName}</small>
        </Link>
      </div>
    );
  };

  const isInitialLoading = loading && items.length === 0;

  const isItemBlocked = useCallback(
    (item) => {
      const byCallback = typeof blockItem === "function" && blockItem(item);
      const byStatus =
        Array.isArray(blockedPostStatuses) &&
        blockedPostStatuses.length > 0 &&
        blockedPostStatuses.includes(item?.status);
      return Boolean(byCallback || byStatus);
    },
    [blockItem, blockedPostStatuses]
  );

  const handleBlockedCardClick = useCallback(
    (e, item) => {
      if (!isItemBlocked(item)) return;
      e.preventDefault();
      toast.info(blockedItemMessage, {
        position: "top-center",
        autoClose: 3000
      });
    },
    [blockedItemMessage, isItemBlocked]
  );

  return (
    <main className="container py-5" dir="rtl">
      <div className="mb-5">
        {titleActions ? (
          <>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
              <h1 className="display-4 fw-bold mb-0">{pageTitle}</h1>
              <div className="d-flex align-items-center gap-2 flex-shrink-0">{titleActions}</div>
            </div>
            <p className="lead text-muted mx-auto text-center" style={{ maxWidth: "700px" }}>
              {description}
            </p>
          </>
        ) : (
          <div className="text-center">
            <h1 className="display-4 fw-bold mb-3">{pageTitle}</h1>
            <p className="lead text-muted mx-auto" style={{ maxWidth: "700px" }}>
              {description}
            </p>
          </div>
        )}
      </div>

      {belowTitle}

      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div className="d-flex gap-2 flex-wrap">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`btn rounded-pill px-4 py-2 ${
                selectedCategory === category.id ? "btn-primary" : "btn-outline-secondary"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div className="d-flex align-items-center gap-2">
          <ViewToggle viewMode={viewMode} onViewChange={handleViewChange} />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="form-control rounded-pill"
            style={{ maxWidth: "250px" }}
          />
        </div>
      </div>

      {isInitialLoading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
          <p className="mt-3 text-muted">جاري التحميل...</p>
        </div>
      )}

      {error && !isInitialLoading && (
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          {usingFallback && (
            <>
              <br />
              <small>يتم عرض بيانات بديلة عند تعذر الاتصال بالمصدر الرئيسي.</small>
            </>
          )}
        </div>
      )}

      {!isInitialLoading && (
        <>
          {filteredItems.length > 0 ? (
            viewMode === "grid" ? (
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                {filteredItems.map((item) => (
                  <div key={item.id} className="col d-flex">
                    <article
                      className="card h-100 shadow-sm border-0 rounded-4 bg-white w-100 d-flex flex-column"
                      style={{
                        filter: isItemBlocked(item) ? "grayscale(0.5)" : "none",
                        opacity: isItemBlocked(item) ? 0.75 : 1
                      }}
                    >
                      <Link
                        href={isItemBlocked(item) ? "#" : `${hrefBase}/${item.id}`}
                        className="text-decoration-none text-dark d-flex flex-column flex-grow-1"
                        onClick={(e) => handleBlockedCardClick(e, item)}
                        aria-disabled={isItemBlocked(item)}
                        tabIndex={isItemBlocked(item) ? -1 : 0}
                        style={{
                          cursor: isItemBlocked(item) ? "not-allowed" : "pointer"
                        }}
                      >
                        {item.featured_image_urls?.full ? (
                          <img
                            src={item.featured_image_urls?.full}
                            alt={stripHtml(item?.title?.rendered || "item")}
                            className="card-img-top"
                            style={{ objectFit: imageFit, width: "100%", maxHeight: imageMaxHeight }}
                          />
                        ) : (
                          <div
                            className="card-img-top d-flex align-items-center justify-content-center bg-dark text-white"
                            style={{ height: imageMaxHeight }}
                          >
                            <i className={`bi ${fallbackIcon}`} style={{ fontSize: "3rem" }}></i>
                          </div>
                        )}

                        <div className="card-body d-flex flex-column flex-grow-1">
                          <h2
                            className="h5 fw-bold mb-2"
                            dangerouslySetInnerHTML={{ __html: item?.title?.rendered || "بدون عنوان" }}
                          ></h2>

                          <span className="d-block mb-2 text-muted">
                            <i className="bi bi-calendar me-1"></i>
                            {formatArabicDate(item.date)}
                          </span>

                          <p
                            className="text-muted mb-0 flex-grow-1"
                            dangerouslySetInnerHTML={{
                              __html: truncateText(item?.excerpt?.rendered || "", 150)
                            }}
                          ></p>
                        </div>
                      </Link>
                      <div className="card-body border-0 pt-0 position-relative" style={{ zIndex: 2 }}>
                        <AuthorInfo authorId={item.author} />
                        {typeof cardFooter === "function" ? cardFooter(item) : null}
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="list-unstyled d-flex flex-column gap-3">
                {filteredItems.map((item) => (
                  <li key={item.id}>
                    <article
                      className="card shadow-sm border-0 rounded-4 bg-white overflow-hidden"
                      style={{
                        filter: isItemBlocked(item) ? "grayscale(0.5)" : "none",
                        opacity: isItemBlocked(item) ? 0.75 : 1
                      }}
                    >
                      <Link
                        href={isItemBlocked(item) ? "#" : `${hrefBase}/${item.id}`}
                        className="text-decoration-none text-dark d-block"
                        onClick={(e) => handleBlockedCardClick(e, item)}
                        aria-disabled={isItemBlocked(item)}
                        tabIndex={isItemBlocked(item) ? -1 : 0}
                        style={{
                          cursor: isItemBlocked(item) ? "not-allowed" : "pointer"
                        }}
                      >
                        <div className="row g-0">
                          <div className="col-12 col-md-4 col-lg-3">
                            {item.featured_image_urls?.full ? (
                              <img
                                src={item.featured_image_urls?.full}
                                alt={stripHtml(item?.title?.rendered || "item")}
                                className="img-fluid w-100 h-100"
                                style={{ objectFit: imageFit, minHeight: "160px", maxHeight: "200px" }}
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center bg-dark text-white w-100"
                                style={{ minHeight: "160px" }}
                              >
                                <i className={`bi ${fallbackIcon}`} style={{ fontSize: "2.5rem" }}></i>
                              </div>
                            )}
                          </div>
                          <div className="col-12 col-md-8 col-lg-9">
                            <div className="card-body d-flex flex-column justify-content-center h-100 pb-2">
                              <h2
                                className="h5 fw-bold mb-2"
                                dangerouslySetInnerHTML={{ __html: item?.title?.rendered || "بدون عنوان" }}
                              ></h2>

                              <span className="d-block mb-2 text-muted">
                                <i className="bi bi-calendar me-1"></i>
                                {formatArabicDate(item.date)}
                              </span>

                              <p
                                className="text-muted mb-0"
                                dangerouslySetInnerHTML={{
                                  __html: truncateText(item?.excerpt?.rendered || "", 200)
                                }}
                              ></p>
                            </div>
                          </div>
                        </div>
                      </Link>
                      <div className="card-body border-0 pt-0 pb-3 position-relative" style={{ zIndex: 2 }}>
                        <AuthorInfo authorId={item.author} />
                        {typeof cardFooter === "function" ? cardFooter(item) : null}
                      </div>
                    </article>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">{emptyMessage}</p>
            </div>
          )}

          {hasMore && (
            <div ref={sentinelRef} className="py-4 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}
